import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMMAuth } from '@machinemetrics/mm-react-tools';
import {
  listTemplates, generateScenario, getScenario, getProjection,
  patchPlan, checkpointPlan, commitPlan,
} from '../../services/planningApi';
import { DEFAULT_COMPANY } from '../../constants/company';
import { TYPE_ORDER as GROUP_ORDER, TYPE_LABEL as GROUP_LABEL, TYPE_COLOR } from '../../constants/machineTypes';
import { fetchSaved, demandOverrideFromSaved, policyFromSaved } from '../../utils/sandboxScenarios';

// ── Rassini Control Center — Slice 1: read-only planning preview ─────────────
// Generate a scenario from a template, then render the deterministic projection:
//   CAPACITY (why) · GANTT (when) · FLOW (consequence) · NET POSITION (does it keep up).
// Editing (drag/resize, reassign, hold, split, commit) lands in Slice 2+.
//
// Order/label/colour come from the shared vocabulary (also used by Live Floor and the Capacity
// Sandbox) rather than a local copy — this page only ever sees SAND_CORE/SAND_MOLD/LATHE/MILL keys
// today because that's what capacity data has, but importing the full order means PRESS/POUR/
// SWISS_LATHE slot into the right place for free if this view ever needs them, instead of silently
// sorting to "wherever the array ended".

const DAY = 86400;

const fmtHours = (sec) => {
  const h = (sec || 0) / 3600;
  return h >= 100 ? `${Math.round(h)}h` : `${h.toFixed(1)}h`;
};
const fmtDur = (sec) => {
  const s = sec || 0;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  const d = s / DAY;
  if (d >= 1) return `${d.toFixed(d >= 10 ? 0 : 1)}d`;
  return `${(s / 3600).toFixed(1)}h`;
};
const fmtNum = (n) => (n == null ? '—' : Math.round(n).toLocaleString('en-US'));
const fmtPct = (x) => (x == null || !isFinite(x) ? '—' : `${Math.round(x * 100)}%`);
const stripSlug = (name) => (name || '').replace(/^[a-z0-9-]+?-(?=[A-Z])/, '');

export default function ControlCenterPage({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const companySlug = searchParams.get('company') || DEFAULT_COMPANY;
  const urlScenario = searchParams.get('scenario') || null;
  const urlPlan     = searchParams.get('plan') || null;

  const auth        = useMMAuth();
  const accessToken = auth?.accessToken ?? null;

  const [templates, setTemplates]     = useState([]);
  const [templateId, setTemplateId]   = useState('');
  const [seed, setSeed]               = useState('');
  const [scenario, setScenario]       = useState(null);
  const [projection, setProjection]   = useState(null);
  const [planId, setPlanId]           = useState(urlPlan);
  const [loading, setLoading]         = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [busy, setBusy]               = useState(false);       // in-flight edit / commit
  const [commitMsg, setCommitMsg]     = useState(null);
  const [error, setError]             = useState(null);
  // Saved sandbox scenarios, so they're openable straight from Flow. Server-backed now (they used
  // to be localStorage, which made them invisible on any other device) — hence a fetch rather than
  // a synchronous initializer. A failure here is silent on purpose: Flow's own job does not depend
  // on this list, and an error banner for an empty convenience strip would be worse than no strip.
  const [saved, setSaved]             = useState([]);
  useEffect(() => {
    let alive = true;
    fetchSaved(companySlug, accessToken).then((rows) => { if (alive) setSaved(rows); }).catch(() => {});
    return () => { alive = false; };
  }, [companySlug, accessToken]);

  // Templates for this company (also gives us partId → partNumber via demandBaseline).
  useEffect(() => {
    let alive = true;
    setError(null);
    listTemplates(companySlug, accessToken)
      .then((t) => { if (!alive) return; setTemplates(t); setTemplateId((prev) => prev || t[0]?.id || ''); })
      .catch((e) => { if (alive) setError(describeErr(e)); });
    return () => { alive = false; };
  }, [companySlug, accessToken]);

  // Load an existing scenario + plan projection when the URL carries them (refresh-safe).
  const loadScenario = useCallback(async (scenarioId, planId) => {
    setLoading(true); setError(null);
    try {
      const sc = await getScenario(scenarioId, accessToken);
      if (!sc) throw new Error('Scenario not found');
      // Prefer the explicit plan, else the latest plan version on the scenario.
      const pid = planId || sc.plans?.[sc.plans.length - 1]?.id;
      const proj = pid ? await getProjection(pid, accessToken) : null;
      setScenario(sc);
      setProjection(proj);
      setPlanId(proj?.plan?.id || pid || null);
      setCommitMsg(null);
    } catch (e) {
      setError(describeErr(e));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (urlScenario) loadScenario(urlScenario, urlPlan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlScenario, urlPlan, accessToken]);

  const onGenerate = async () => {
    if (!templateId) return;
    setGenerating(true); setError(null);
    try {
      const { scenarioId, planId } = await generateScenario(templateId, { seed }, accessToken);
      const next = new URLSearchParams(searchParams);
      next.set('scenario', scenarioId);
      next.set('plan', planId);
      setSearchParams(next, { replace: false });
      await loadScenario(scenarioId, planId);
    } catch (e) {
      setError(describeErr(e));
    } finally {
      setGenerating(false);
    }
  };

  // Open a saved sandbox scenario here: generate a server scenario+plan from its stored config
  // (demand + flow policy + castings) and load it, exactly as if it had been generated on this page.
  const openSaved = async (scn) => {
    const tid = templateId || templates[0]?.id;
    if (!tid) return;
    setGenerating(true); setError(null);
    try {
      const { scenarioId, planId: pid } = await generateScenario(tid, {
        demandOverride: demandOverrideFromSaved(scn), policy: policyFromSaved(scn),
      }, accessToken);
      const next = new URLSearchParams(searchParams);
      next.set('scenario', scenarioId);
      next.set('plan', pid);
      setSearchParams(next, { replace: false });
      await loadScenario(scenarioId, pid);
    } catch (e) {
      setError(describeErr(e));
    } finally {
      setGenerating(false);
    }
  };

  // Apply a single edit to the open DRAFT; the response IS the fresh projection (+ lots).
  const applyEdit = useCallback(async (edit) => {
    if (!planId) return;
    setBusy(true); setError(null);
    try {
      const proj = await patchPlan(planId, edit, accessToken);
      setProjection(proj);
      setPlanId(proj?.plan?.id || planId);
    } catch (e) {
      setError(describeErr(e));
    } finally {
      setBusy(false);
    }
  }, [planId, accessToken]);

  // Fork the current DRAFT into a new editable MANUAL version.
  const onCheckpoint = async () => {
    if (!planId) return;
    setBusy(true); setError(null);
    try {
      const { planId: newId } = await checkpointPlan(planId, accessToken);
      const next = new URLSearchParams(searchParams);
      next.set('plan', newId);
      setSearchParams(next, { replace: false });
      const proj = await getProjection(newId, accessToken);
      setProjection(proj);
      setPlanId(newId);
      setCommitMsg(null);
    } catch (e) {
      setError(describeErr(e));
    } finally {
      setBusy(false);
    }
  };

  // Commit → compile into live WorkOrders; plan locks to COMMITTED.
  const onCommit = async () => {
    if (!planId || !scenario) return;
    if (!window.confirm('Commit this plan? It compiles into live WorkOrders on the floor and the plan becomes read-only.')) return;
    setBusy(true); setError(null);
    try {
      const res = await commitPlan(scenario.id, planId, accessToken);
      await loadScenario(scenario.id, planId);
      setCommitMsg(`Committed — ${res.workOrders} work orders released to the floor.`);
    } catch (e) {
      setError(describeErr(e));
    } finally {
      setBusy(false);
    }
  };

  // partId → { partNumber, weeklyQty } from the scenario's template baseline.
  const partMeta = useMemo(() => {
    const tmpl = templates.find((t) => t.id === scenario?.templateId) || templates.find((t) => t.id === templateId);
    const map = {};
    for (const b of (tmpl?.demandBaseline || [])) map[b.partId] = { partNumber: b.partNumber, weeklyQty: b.weeklyQty };
    return map;
  }, [templates, scenario, templateId]);

  // machineId → { name, group } from the frozen roster.
  const machineMeta = useMemo(() => {
    const map = {};
    for (const m of (scenario?.problemSnapshot?.machineRoster || [])) {
      map[m.machineId] = { name: stripSlug(m.name), group: m.resourceGroup };
    }
    return map;
  }, [scenario]);

  return (
    <div style={{ padding: '32px 36px', maxWidth: 1280 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
        {!embedded && (
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--cn-text)', margin: 0 }}>Flow Planner</h1>
        )}
        <span style={{ fontSize: 12, color: 'var(--cn-dimmed)' }}>Mode 2 · plan &amp; simulate · {companySlug}</span>
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--cn-muted)', margin: '0 0 20px', maxWidth: 720, lineHeight: 1.5 }}>
        Generate a period's demand, then preview the deterministic plan before committing it to the floor.
        <b style={{ color: 'var(--cn-text)' }}> Capacity</b> is why, <b style={{ color: 'var(--cn-text)' }}>Gantt</b> is when,
        <b style={{ color: 'var(--cn-text)' }}> Flow</b> is the consequence, and <b style={{ color: 'var(--cn-text)' }}>Net Position</b> is
        whether the factory keeps up.
      </p>

      {/* Generation controls */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap',
        background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.15)',
        borderRadius: 8, padding: '16px 18px', marginBottom: 22,
      }}>
        <Field label="Template">
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            style={selectStyle}
          >
            {templates.length === 0 && <option value="">No templates</option>}
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Seed (optional)">
          <input
            value={seed}
            onChange={(e) => setSeed(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="random"
            style={{ ...selectStyle, width: 120 }}
          />
        </Field>
        <button
          onClick={onGenerate}
          disabled={!templateId || generating}
          style={{
            background: (!templateId || generating) ? 'var(--cn-surface2)' : 'var(--cn-gold)',
            color: (!templateId || generating) ? 'var(--cn-dimmed)' : '#1a1206',
            border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 13, fontWeight: 700,
            fontFamily: 'inherit', cursor: (!templateId || generating) ? 'default' : 'pointer',
          }}
        >
          {generating ? 'Generating…' : 'Generate scenario'}
        </button>
        {scenario && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 18, alignItems: 'center' }}>
            <Meta label="Seed" value={scenario.seed} />
            <Meta label="Period" value={`#${scenario.periodIndex}`} />
            <Meta label="Status" value={scenario.status} />
          </div>
        )}
      </div>

      {/* Saved sandbox scenarios — the ones built in the Capacity Sandbox (persisted in this browser).
          Open one to generate + preview it here without re-entering its demand and flow policy. */}
      {saved.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 22 }}>
          <span style={{ fontSize: 10, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--cn-dimmed)' }}>Saved scenarios</span>
          {saved.map((s) => (
            <button key={s.id} onClick={() => openSaved(s)} disabled={generating}
              title="Generate & preview this saved scenario here"
              style={{
                fontSize: 12, fontWeight: 600, color: generating ? 'var(--cn-dimmed)' : 'var(--cn-text)',
                background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.24)', borderRadius: 8,
                padding: '6px 12px', cursor: generating ? 'default' : 'pointer', fontFamily: 'inherit',
              }}>
              {s.name} <span style={{ color: 'var(--cn-dimmed)' }}>→</span>
            </button>
          ))}
        </div>
      )}

      {error && (
        <div style={{
          background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.4)',
          borderRadius: 8, padding: '14px 16px', color: '#e8a89f', fontSize: 13, marginBottom: 20, lineHeight: 1.5,
        }}>
          {error}
        </div>
      )}

      {loading && <div style={{ color: 'var(--cn-muted)', padding: '20px 0' }}>Loading projection…</div>}

      {!loading && !scenario && !error && (
        <div style={{ color: 'var(--cn-muted)', padding: '40px 0', textAlign: 'center', fontSize: 14 }}>
          Pick a template and generate a scenario to preview the plan.
        </div>
      )}

      {!loading && scenario && projection && (
        <>
          <PlanToolbar
            plan={projection.plan}
            busy={busy}
            commitMsg={commitMsg}
            onCheckpoint={onCheckpoint}
            onCommit={onCommit}
          />
          <NetPosition flow={projection.flow} />
          <Capacity capacity={projection.capacity} />
          <PlanEditor
            lots={projection.lots}
            roster={projection.roster}
            partMeta={partMeta}
            editable={projection.plan?.status === 'DRAFT'}
            busy={busy}
            onEdit={applyEdit}
          />
          <Gantt schedule={projection.schedule} machineMeta={machineMeta} partMeta={partMeta} />
          <Flow flow={projection.flow} />
          <DemandTable demand={scenario.demand} partMeta={partMeta} flow={projection.flow} />
        </>
      )}
    </div>
  );
}

// ── Net Position hero ─────────────────────────────────────────────────────────
function NetPosition({ flow }) {
  const demand   = flow.demandTotal ?? 0;
  const finished = flow.finishedByDue ?? 0;
  const backlog  = flow.backlog ?? 0;
  const surplus  = flow.surplus ?? 0;
  const keepsUp  = backlog === 0;
  const fillPct  = demand > 0 ? Math.min(1, finished / demand) : 0;
  return (
    <Section title="Net Position" subtitle="Finished-by-due vs demand — reality, not intent">
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <div style={{
            fontSize: 40, fontWeight: 800, lineHeight: 1,
            color: keepsUp ? 'var(--cn-success)' : 'var(--cn-danger)',
          }}>
            {keepsUp ? `+${fmtNum(surplus)}` : `−${fmtNum(backlog)}`}
          </div>
          <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 6 }}>
            {keepsUp ? 'Surplus by due date' : 'Backlog by due date'}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--cn-muted)', marginBottom: 6 }}>
            <span>Finished by due: <b style={{ color: 'var(--cn-text)' }}>{fmtNum(finished)}</b></span>
            <span>Demand: <b style={{ color: 'var(--cn-text)' }}>{fmtNum(demand)}</b></span>
          </div>
          <div style={{ height: 14, background: 'var(--cn-surface2)', borderRadius: 7, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${fillPct * 100}%`,
              background: keepsUp ? 'var(--cn-success)' : 'var(--cn-danger)',
              transition: 'width 0.4s',
            }} />
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--cn-dimmed)', marginTop: 8 }}>
            {fmtPct(fillPct)} of demand met by the due date · total projected finished {fmtNum(flow.finishedProjected)}
          </div>
        </div>
      </div>
    </Section>
  );
}

// ── Capacity panel ────────────────────────────────────────────────────────────
function Capacity({ capacity }) {
  const groups = GROUP_ORDER.filter((g) => capacity[g]).concat(
    Object.keys(capacity).filter((g) => !GROUP_ORDER.includes(g)),
  );
  return (
    <Section title="Capacity" subtitle="Required vs available per resource group — the “why”">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {groups.map((g) => {
          const c = capacity[g];
          const util = c.utilization;
          const over = util > 1;
          const near = util > 0.85 && util <= 1;
          const barColor = over ? 'var(--cn-danger)' : near ? 'var(--cn-warning)' : 'var(--cn-success)';
          const fill = Math.min(1, isFinite(util) ? util : 1);
          const heldFrac = c.requiredSec > 0 ? c.heldSec / c.requiredSec : 0;
          return (
            <div key={g}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 5 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 130 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: TYPE_COLOR[g] || 'var(--cn-muted)' }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--cn-text)' }}>{GROUP_LABEL[g] || g}</span>
                  <span style={{ fontSize: 11, color: 'var(--cn-dimmed)' }}>× {c.machines}</span>
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: barColor, marginLeft: 'auto' }}>{fmtPct(util)}</span>
                {!c.feasible && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'var(--cn-danger)', border: '1px solid var(--cn-danger)', borderRadius: 999, padding: '1px 7px',
                  }}>over</span>
                )}
              </div>
              <div style={{ height: 12, background: 'var(--cn-surface2)', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                <div style={{ height: '100%', width: `${fill * 100}%`, background: barColor, transition: 'width 0.4s' }} />
                {heldFrac > 0 && (
                  <div style={{
                    position: 'absolute', top: 0, height: '100%',
                    left: `${Math.max(0, fill - heldFrac) * 100}%`, width: `${Math.min(fill, heldFrac) * 100}%`,
                    backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.35) 0 4px, transparent 4px 8px)',
                  }} />
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--cn-muted)', marginTop: 4 }}>
                Required <b style={{ color: 'var(--cn-text)' }}>{fmtHours(c.requiredSec)}</b>
                {' · '}Available <b style={{ color: 'var(--cn-text)' }}>{fmtHours(c.availableSec)}</b>
                {c.heldSec > 0 && <> · Held <b style={{ color: 'var(--cn-text)' }}>{fmtHours(c.heldSec)}</b></>}
                {' · '}Setups {fmtHours(c.setupSec)}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ── Gantt (read-only) ─────────────────────────────────────────────────────────
const LABEL_W = 120;
const ROW_H = 34;

function Gantt({ schedule, machineMeta, partMeta }) {
  const containerRef = useRef(null);
  const [chartW, setChartW] = useState(760);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setChartW(Math.max(120, w - LABEL_W));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const ops = schedule?.ops || [];
  // Row order: frozen roster order (so idle machines still get a lane); fall back to
  // whatever machines the schedule touched.
  const rosterIds = Object.keys(machineMeta);
  const machineIds = rosterIds.length ? rosterIds : Object.keys(schedule?.machines || {});

  const maxSec = ops.reduce((m, o) => Math.max(m, o.schedEnd || 0), 0) || DAY;
  const pxPerSec = chartW / maxSec;

  // Day ticks — keep ~8–14 labels across the span.
  const spanDays = maxSec / DAY;
  const dayStep = spanDays <= 8 ? 1 : spanDays <= 16 ? 2 : spanDays <= 32 ? 4 : 7;
  const ticks = [];
  for (let d = 0; d * DAY <= maxSec; d += dayStep) ticks.push({ day: d, px: d * DAY * pxPerSec });

  const opsByMachine = useMemo(() => {
    const by = {};
    for (const o of ops) (by[o.machineId] ||= []).push(o);
    return by;
  }, [ops]);

  return (
    <Section title="Gantt" subtitle={`Chunk-scheduled plan · ${spanDays.toFixed(1)} days to complete · offset from plan start`}>
      <div ref={containerRef} style={{
        background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.1)',
        borderRadius: 8, overflow: 'hidden', userSelect: 'none',
      }}>
        {/* Axis */}
        <div style={{ display: 'flex', height: 26, borderBottom: '1px solid rgba(168,144,96,0.12)' }}>
          <div style={{ width: LABEL_W, flexShrink: 0, background: 'var(--cn-surface2)', borderRight: '1px solid rgba(168,144,96,0.12)' }} />
          <div style={{ position: 'relative', flex: 1 }}>
            {ticks.map((t) => (
              <div key={t.day} style={{ position: 'absolute', left: t.px, top: 0, bottom: 0 }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 1, background: 'rgba(168,144,96,0.15)' }} />
                <span style={{ position: 'absolute', left: 4, top: 6, fontSize: 10, color: 'var(--cn-muted)', whiteSpace: 'nowrap' }}>
                  Day {t.day}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        {machineIds.map((mid, i) => {
          const meta = machineMeta[mid] || { name: mid.slice(0, 6), group: '' };
          const color = TYPE_COLOR[meta.group] || 'var(--cn-muted)';
          const isLast = i === machineIds.length - 1;
          return (
            <div key={mid} style={{ display: 'flex', height: ROW_H, borderBottom: isLast ? 'none' : '1px solid rgba(168,144,96,0.06)' }}>
              <div style={{
                width: LABEL_W, flexShrink: 0, background: 'var(--cn-surface)',
                borderRight: '1px solid rgba(168,144,96,0.12)', display: 'flex', alignItems: 'center', gap: 7, padding: '0 12px',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: 1, background: color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cn-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {meta.name}
                </span>
              </div>
              <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                {ticks.map((t) => (
                  <div key={t.day} style={{ position: 'absolute', left: t.px, top: 0, bottom: 0, width: 1, background: 'rgba(168,144,96,0.06)' }} />
                ))}
                {(opsByMachine[mid] || []).map((op) => {
                  const left = op.schedStart * pxPerSec;
                  const width = Math.max(2, (op.schedEnd - op.schedStart) * pxPerSec);
                  const pn = partMeta[op.partId]?.partNumber || op.partId?.slice(0, 6);
                  return (
                    <div
                      key={op.opId}
                      title={`${pn} · OP${op.sequence} · ${fmtDur(op.schedStart)}→${fmtDur(op.schedEnd)} (${fmtDur(op.schedEnd - op.schedStart)})`}
                      style={{
                        position: 'absolute', left, width: width - 1, top: 6, height: ROW_H - 12,
                        background: color, opacity: 0.85, borderRadius: 3,
                        display: 'flex', alignItems: 'center', paddingLeft: 4, overflow: 'hidden',
                      }}
                    >
                      {width > 34 && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {pn}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ── Flow stats ────────────────────────────────────────────────────────────────
function Flow({ flow }) {
  const wip = flow.wip || {};
  return (
    <Section title="Flow" subtitle="Makespan, changeover, idle capacity, and WIP piling up between stages">
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <StatTile label="Makespan" value={fmtDur(flow.makespanSec)} hint="first start → last finish" />
        <StatTile label="Setup" value={fmtHours(flow.setupHours * 3600)} hint="total changeover" />
        <StatTile label="Idle capacity" value={fmtHours(flow.idleHours * 3600)} hint="starvation inside spans" />
        <StatTile label="Finished (all)" value={fmtNum(flow.finishedProjected)} hint="OP40 completions" />
      </div>
      {Object.keys(wip).length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--cn-dimmed)', marginBottom: 8 }}>
            WIP between stages
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.entries(wip).map(([iface, w]) => {
              const label = iface.split('->').map((g) => GROUP_LABEL[g] || g).join(' → ');
              return (
                <div key={iface} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12 }}>
                  <span style={{ minWidth: 200, color: 'var(--cn-text)' }}>{label}</span>
                  <span style={{ color: 'var(--cn-muted)' }}>peak <b style={{ color: 'var(--cn-text)' }}>{fmtNum(w.maxUnits)}</b></span>
                  <span style={{ color: 'var(--cn-muted)' }}>avg <b style={{ color: 'var(--cn-text)' }}>{fmtNum(w.avgUnits)}</b></span>
                  <span style={{ color: 'var(--cn-dimmed)', fontSize: 11 }}>units</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
}

// ── Demand table ──────────────────────────────────────────────────────────────
function DemandTable({ demand, partMeta, flow }) {
  const rows = (demand || []).slice().sort((a, b) => b.quantity - a.quantity);
  const totalDemand = rows.reduce((s, d) => s + d.quantity, 0);
  return (
    <Section title="Demand" subtitle="This period's drawn demand vs baseline (seeded)">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 520 }}>
          <thead>
            <tr style={{ color: 'var(--cn-dimmed)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              <Th align="left">Part</Th>
              <Th align="right">Baseline</Th>
              <Th align="right">Demand</Th>
              <Th align="right">Variance</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const meta = partMeta[d.partId] || {};
              const base = d.baselineQuantity || meta.weeklyQty || 0;
              const delta = base > 0 ? (d.quantity - base) / base : 0;
              const up = delta >= 0;
              return (
                <tr key={d.partId} style={{ borderTop: '1px solid rgba(168,144,96,0.08)' }}>
                  <Td align="left"><b style={{ color: 'var(--cn-gold)' }}>{meta.partNumber || d.partId.slice(0, 8)}</b></Td>
                  <Td align="right">{fmtNum(base)}</Td>
                  <Td align="right"><b style={{ color: 'var(--cn-text)' }}>{fmtNum(d.quantity)}</b></Td>
                  <Td align="right">
                    <span style={{ color: up ? 'var(--cn-success)' : 'var(--cn-danger)' }}>
                      {up ? '+' : '−'}{Math.abs(Math.round(delta * 100))}%
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid rgba(168,144,96,0.2)' }}>
              <Td align="left"><b style={{ color: 'var(--cn-text)' }}>Total</b></Td>
              <Td align="right" />
              <Td align="right"><b style={{ color: 'var(--cn-text)' }}>{fmtNum(totalDemand)}</b></Td>
              <Td align="right" />
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{ fontSize: 12, color: 'var(--cn-muted)', marginTop: 12, lineHeight: 1.5 }}>
        Plan finishes <b style={{ color: 'var(--cn-text)' }}>{fmtNum(flow.finishedByDue)}</b> of{' '}
        <b style={{ color: 'var(--cn-text)' }}>{fmtNum(flow.demandTotal)}</b> by the due date —{' '}
        {flow.backlog > 0
          ? <span style={{ color: 'var(--cn-danger)' }}>backlog of {fmtNum(flow.backlog)}.</span>
          : <span style={{ color: 'var(--cn-success)' }}>demand met.</span>}
      </div>
    </Section>
  );
}

// ── Plan toolbar: version/status + checkpoint + commit ───────────────────────
function PlanToolbar({ plan, busy, commitMsg, onCheckpoint, onCommit }) {
  const status = plan?.status || 'DRAFT';
  const editable = status === 'DRAFT';
  const statusColor = status === 'DRAFT' ? 'var(--cn-info)' : status === 'COMMITTED' ? 'var(--cn-success)' : 'var(--cn-muted)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.15)',
      borderRadius: 8, padding: '12px 16px', marginBottom: 18,
      // Keep plan status + Saving…/commit feedback in view when editing lots or reading the
      // Gantt further down the tall page (Doherty — feedback where attention is).
      position: 'sticky', top: 0, zIndex: 5,
    }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--cn-text)' }}>Plan v{plan?.version}</span>
      <span style={{ fontSize: 11, color: 'var(--cn-dimmed)' }}>{plan?.source}</span>
      <span style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: statusColor, border: `1px solid ${statusColor}`, borderRadius: 999, padding: '2px 9px',
      }}>{status}</span>
      {busy && <span style={{ fontSize: 11, color: 'var(--cn-gold)' }}>Saving…</span>}
      {/* Commit is the peak moment — confirm it as a real success pill, not a whisper (Peak-End). */}
      {commitMsg && (
        <span style={{
          fontSize: 12, color: 'var(--cn-success)', fontWeight: 700,
          background: 'rgba(46,160,67,0.12)', border: '1px solid var(--cn-success)',
          borderRadius: 999, padding: '3px 10px',
        }}>✓ {commitMsg}</span>
      )}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={onCheckpoint} disabled={busy} style={ghostBtn(busy)}>Checkpoint</button>
        {/* Divider + extra gap so the irreversible Commit isn't a mis-click away (Fitts). */}
        <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(168,144,96,0.2)', margin: '0 4px' }} />
        <button onClick={onCommit} disabled={busy || !editable} style={primaryBtn(busy || !editable)}>Commit to floor</button>
      </div>
    </div>
  );
}

// ── Plan editor: editable lots × ops ─────────────────────────────────────────
function PlanEditor({ lots, roster, partMeta, editable, busy, onEdit }) {
  const byGroup = useMemo(() => {
    const g = {};
    for (const m of (roster || [])) (g[m.resourceGroup] ||= []).push(m);
    return g;
  }, [roster]);
  const sorted = (lots || []).slice().sort((a, b) =>
    (a.priority - b.priority) ||
    (partMeta[a.partId]?.partNumber || a.partId).localeCompare(partMeta[b.partId]?.partNumber || b.partId));

  return (
    <Section
      title="Plan editor"
      subtitle={editable
        ? 'Resize a lot, reassign or hold an operation, or set priority — every edit re-plans instantly'
        : 'This plan is committed — read-only. Checkpoint a new version to edit.'}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
          <thead>
            <tr style={{ color: 'var(--cn-dimmed)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              <Th align="left">Part</Th>
              <Th align="right">Qty</Th>
              <Th align="right">Prio</Th>
              {[['OP10', 'Sand Core'], ['OP20', 'Sand Mold'], ['OP30', 'Lathe'], ['OP40', 'Mill']].map(([op, g]) => (
                <th key={op} style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 700 }}>
                  {op} <span style={{ color: 'var(--cn-muted)', fontWeight: 400 }}>{g}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((lot) => (
              <LotRow key={lot.id} lot={lot} partMeta={partMeta} byGroup={byGroup} editable={editable} busy={busy} onEdit={onEdit} />
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginTop: 10 }}>
        {sorted.length} lots · <b style={{ color: 'var(--cn-muted)' }}>❚❚</b> holds an operation (moves its load to Held; drops it from the schedule), <b style={{ color: 'var(--cn-muted)' }}>▶</b> resumes it · the machine dropdown reassigns within the resource group.
      </div>
    </Section>
  );
}

function LotRow({ lot, partMeta, byGroup, editable, busy, onEdit }) {
  const pn = partMeta[lot.partId]?.partNumber || lot.partId.slice(0, 8);
  const opBySeq = {};
  for (const o of lot.ops) opBySeq[o.sequence] = o;
  return (
    <tr style={{ borderTop: '1px solid rgba(168,144,96,0.08)' }}>
      <td style={{ padding: '6px 10px' }}><b style={{ color: 'var(--cn-gold)' }}>{pn}</b></td>
      <td style={{ padding: '6px 10px', textAlign: 'right' }}>
        <NumberCell value={lot.quantity} disabled={!editable || busy} width={72}
          onCommit={(v) => onEdit({ op: 'resizeLot', lotId: lot.id, quantity: v })} />
      </td>
      <td style={{ padding: '6px 10px', textAlign: 'right' }}>
        <NumberCell value={lot.priority} disabled={!editable || busy} width={52}
          onCommit={(v) => onEdit({ op: 'setPriority', lotId: lot.id, priority: v })} />
      </td>
      {[10, 20, 30, 40].map((seq) => (
        <td key={seq} style={{ padding: '6px 10px' }}>
          <OpCell op={opBySeq[seq]} machines={byGroup[opBySeq[seq]?.machineType] || []} editable={editable} busy={busy} onEdit={onEdit} />
        </td>
      ))}
    </tr>
  );
}

function OpCell({ op, machines, editable, busy, onEdit }) {
  if (!op) return <span style={{ color: 'var(--cn-dimmed)' }}>—</span>;
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', opacity: op.held ? 0.55 : 1 }}>
      <select
        value={op.assignedMachineId || ''}
        disabled={!editable || busy || op.held}
        onChange={(e) => onEdit({ op: 'reassignOp', opId: op.id, machineId: e.target.value })}
        style={{
          background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.2)', borderRadius: 4,
          color: 'var(--cn-text)', padding: '3px 4px', fontSize: 11, fontFamily: 'inherit', maxWidth: 92,
        }}
      >
        {machines.length === 0 && <option value="">—</option>}
        {machines.map((m) => <option key={m.id} value={m.id}>{stripSlug(m.name)}</option>)}
      </select>
      <button
        title={op.held ? 'Resume operation' : 'Hold operation'}
        disabled={!editable || busy}
        onClick={() => onEdit({ op: 'holdOp', opId: op.id, held: !op.held })}
        style={{
          background: op.held ? 'var(--cn-warning)' : 'transparent',
          border: `1px solid ${op.held ? 'var(--cn-warning)' : 'rgba(168,144,96,0.3)'}`,
          borderRadius: 4, color: op.held ? '#1a1206' : 'var(--cn-muted)',
          width: 22, height: 22, fontSize: 11, fontWeight: 800, lineHeight: 1,
          cursor: (!editable || busy) ? 'default' : 'pointer', flexShrink: 0, fontFamily: 'inherit',
        }}
      >
        {op.held ? '▶' : '❚❚'}
      </button>
    </div>
  );
}

// Uncontrolled number input; commits on blur/Enter. Remounts (key) when the server value changes.
function NumberCell({ value, disabled, width = 72, onCommit }) {
  return (
    <input
      key={value}
      type="number"
      min={1}
      defaultValue={value}
      disabled={disabled}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      onBlur={(e) => {
        const v = Math.max(1, Math.round(Number(e.target.value) || value));
        if (v !== value) onCommit(v); else e.target.value = value;
      }}
      style={{
        background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.2)', borderRadius: 4,
        color: 'var(--cn-text)', padding: '4px 6px', fontSize: 12, fontFamily: 'inherit', width, textAlign: 'right',
      }}
    />
  );
}

const ghostBtn = (disabled) => ({
  background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.25)', borderRadius: 6,
  color: disabled ? 'var(--cn-dimmed)' : 'var(--cn-text)', padding: '7px 14px', fontSize: 12, fontWeight: 700,
  fontFamily: 'inherit', cursor: disabled ? 'default' : 'pointer',
});
const primaryBtn = (disabled) => ({
  background: disabled ? 'var(--cn-surface2)' : 'var(--cn-success)', border: 'none', borderRadius: 6,
  color: disabled ? 'var(--cn-dimmed)' : '#fff', padding: '7px 14px', fontSize: 12, fontWeight: 700,
  fontFamily: 'inherit', cursor: disabled ? 'default' : 'pointer',
});

// ── Small building blocks ─────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <section style={{
      background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.12)',
      borderRadius: 10, padding: '18px 20px', marginBottom: 18,
    }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, color: 'var(--cn-text)', margin: 0, letterSpacing: '0.02em' }}>{title}</h2>
        {subtitle && <div style={{ fontSize: 11.5, color: 'var(--cn-dimmed)', marginTop: 3 }}>{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function StatTile({ label, value, hint }) {
  return (
    <div style={{
      flex: '1 1 140px', minWidth: 140, background: 'var(--cn-surface2)',
      border: '1px solid rgba(168,144,96,0.1)', borderRadius: 8, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cn-dimmed)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--cn-text)', margin: '4px 0 2px' }}>{value}</div>
      {hint && <div style={{ fontSize: 10.5, color: 'var(--cn-muted)' }}>{hint}</div>}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cn-dimmed)' }}>{label}</span>
    {children}
  </label>
);
const Meta = ({ label, value }) => (
  <div style={{ textAlign: 'right' }}>
    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cn-dimmed)' }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cn-text)' }}>{value}</div>
  </div>
);
const Th = ({ align, children }) => <th style={{ textAlign: align, padding: '6px 10px', fontWeight: 700 }}>{children}</th>;
const Td = ({ align, children }) => <td style={{ textAlign: align, padding: '8px 10px', color: 'var(--cn-muted)' }}>{children}</td>;

const selectStyle = {
  background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.2)', borderRadius: 5,
  color: 'var(--cn-text)', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', minWidth: 180,
};

function describeErr(e) {
  const status = e?.response?.status;
  if (status === 401) {
    return 'Planning API requires MachineMetrics authentication (401). Run the app behind MMProvider (fill client/public/default.json) or use the deployed site — local no-auth dev cannot reach these routes.';
  }
  if (status === 403) return 'Access denied (403) — you are not the owner of this company.';
  return e?.response?.data?.error || e?.message || 'Request failed';
}
